import type { GhGroup } from "@kwp/contracts";
import { THREADS_GROUP } from "@kwp/firestore";
import { collectionGroup, getCountFromServer, limit, onSnapshot, orderBy, query, where, type QueryConstraint } from "firebase/firestore";
import { useEffect, useState } from "react";
import { useClients } from "../../lib/clients";
import { GH_GROUP_VIEWS } from "./sources";

/**
 * GitHub's unread counts (architecture §6.7, WP-062): for the collapsed "GitHub · n (k need you)"
 * row under Notifications and for each group's tab. A `limit(1)` listener on the newest GitHub
 * inbox thread says whether there is any; each time it fires, and whenever `refresh` changes (the
 * list on screen changed), the counts are read again with count aggregations, never the threads.
 */
export interface GitHubSummary {
  /** Unread GitHub conversations in the inbox; null when the count could not be read. */
  unread: number | null;
  groups: Record<GhGroup, number | null>;
}

const NO_COUNTS = (): GitHubSummary["groups"] => ({ "needs-me": null, security: null, conversation: null, activity: null, ci: null });

/** null: still loading, or no GitHub mail in the inbox. `accountIds` null: every account. */
export function useGitHubSummary(uid: string, accountIds: readonly string[] | null, enabled: boolean, refresh: string): GitHubSummary | null | "none" {
  const { firebase } = useClients();
  const key = JSON.stringify(accountIds);
  const [tick, setTick] = useState(0);
  const [exists, setExists] = useState<boolean | null>(null);
  const [summary, setSummary] = useState<GitHubSummary | null>(null);

  const ids = JSON.parse(key) as string[] | null;
  const off = !enabled || (ids !== null && ids.length === 0);

  useEffect(() => {
    setExists(null);
    if (off) return undefined;
    const scoped = JSON.parse(key) as string[] | null;
    const scope: QueryConstraint[] = [where("ownerUid", "==", uid)];
    if (scoped !== null) scope.push(scoped.length === 1 ? where("accountId", "==", scoped[0]) : where("accountId", "in", scoped));
    const newest = query(collectionGroup(firebase.db, THREADS_GROUP), ...scope, where("location", "==", "inbox"), where("source.app", "==", "github"), orderBy("lastMessageAt", "desc"), limit(1));
    return onSnapshot(
      newest,
      (snap) => {
        setExists(snap.size > 0);
        setTick((t) => t + 1);
      },
      () => setExists(false),
    );
  }, [firebase.db, uid, key, off]);

  useEffect(() => {
    if (off || exists !== true) return undefined;
    let live = true;
    const scoped = JSON.parse(key) as string[] | null;
    const base: QueryConstraint[] = [where("ownerUid", "==", uid)];
    if (scoped !== null) base.push(scoped.length === 1 ? where("accountId", "==", scoped[0]) : where("accountId", "in", scoped));
    base.push(where("location", "==", "inbox"));
    const count = (field: string, value: string) =>
      getCountFromServer(query(collectionGroup(firebase.db, THREADS_GROUP), ...base, where(field, "==", value), where("unread", "==", true))).then(
        (c): number | null => c.data().count,
        () => null,
      );
    void Promise.all([count("source.app", "github"), ...GH_GROUP_VIEWS.map((g) => count("ghGroup", g.key))]).then(([unread, ...each]) => {
      if (!live) return;
      const groups = NO_COUNTS();
      GH_GROUP_VIEWS.forEach((g, i) => {
        const n = each[i];
        groups[g.key] = n === undefined ? null : n;
      });
      setSummary({ unread: unread === undefined ? null : unread, groups });
    });
    return () => {
      live = false;
    };
  }, [firebase.db, uid, key, off, exists, tick, refresh]);

  if (off || exists === false) return "none";
  if (exists === null) return null;
  return summary;
}
